import { ReactNode, useState } from 'react';

import {
  GetFormalOffersResponse,
  GetIwocapayPaymentCapturedResponse,
} from '@iwoca/lapi-client/edge';
import { Button, Skeleton, Table } from '@iwoca/orion';

import { useGetFormalOffers } from '../../../../api/lending/lapiHooks';
import { formatUnfundedCapturedPaymentDates } from '../../Dashboard.helpers';
import { CapturedPayment } from '../../Dashboard.types';
import { formatCurrency, formatRate } from '../../utils/currency';
import { formatDate } from '../../utils/date';
import { useDrawDownRepaymentScheduleIwocaPay } from '../OfferChoiceForm/useDrawDownRepaymentScheduleIwocaPay';
import { mapOfferDurationToProduct } from '../OfferChoiceForm/utils/mapOfferDurationToProduct';

type ScheduleEntry = {
  date: string;
  principal: number;
  interest: number;
  total: number;
};

const getRelevantFormalOffer = ({
  formalOffers,
  sellerProduct,
}: {
  formalOffers: NonNullable<GetFormalOffersResponse['data']>['formal_offers'];
  sellerProduct: GetIwocapayPaymentCapturedResponse['data'][number]['product'];
}) => {
  if (!formalOffers) return null;

  return formalOffers.find((offer) => {
    const offerSellerProduct = mapOfferDurationToProduct(offer);
    return offerSellerProduct === sellerProduct;
  });
};

export const NewOrderSummary = ({
  title,
  plan,
}: {
  title: ReactNode;
  plan: CapturedPayment;
}) => {
  const { formalOffers, loadingFormalOffers } = useGetFormalOffers();
  const offer = getRelevantFormalOffer({
    formalOffers: formalOffers?.formal_offers ?? [],
    sellerProduct: plan.sellerProduct,
  });

  const {
    drawDownRepaymentScheduleIwocapay,
    interestRate,
    loadingDrawDownRepaymentScheduleIwocapay,
  } = useDrawDownRepaymentScheduleIwocaPay({
    offer: offer!,
    payLink: plan.payLink,
  });

  if (loadingDrawDownRepaymentScheduleIwocapay || loadingFormalOffers)
    return <Skeleton />;

  if (!offer) return null;
  if (!drawDownRepaymentScheduleIwocapay) return null;

  const schedule =
    drawDownRepaymentScheduleIwocapay.schedule as ScheduleEntry[];
  const totalPayable = drawDownRepaymentScheduleIwocapay.summary.total_payable!;
  const totalInterest =
    drawDownRepaymentScheduleIwocapay.summary.total_interest!;
  const totalPrinciple = schedule.reduce(
    (total, entry) => total + entry.principal,
    0,
  );

  const overrideScheduleDates = formatUnfundedCapturedPaymentDates(plan);

  return (
    <div className="flex flex-col">
      <h2 className="m-0">{title || 'Your payment plan'}</h2>

      <h3 data-testid="order-amount-title" className="mb-5 text-[#184681]">
        (Today&apos;s outstanding amount: {formatCurrency(totalPayable)})
      </h3>

      <RepaymentsSchedule
        schedule={schedule}
        totalInterest={totalInterest}
        totalPrinciple={totalPrinciple}
        totalPayable={totalPayable}
        overrideScheduleDates={overrideScheduleDates}
      />

      <p
        data-testid="interest-disclaimer"
        className="font-light mx-11 mb-0 text-center text-[#184681] sm:mx-0"
      >
        *Your interest rate is{' '}
        {interestRate !== undefined ? formatRate(interestRate) : '0.0%'} per
        month.
      </p>
    </div>
  );
};

const RepaymentsSchedule = ({
  schedule,
  totalInterest,
  totalPrinciple,
  totalPayable,
  overrideScheduleDates,
}: {
  schedule: ScheduleEntry[];
  totalInterest: number;
  totalPrinciple: number;
  totalPayable: number;
  overrideScheduleDates?: (date: string, index: number) => string;
}) => {
  const [showAll, setShowAll] = useState(false);

  const FormatRepaymentDate = ({
    repayment,
    index,
  }: {
    repayment: ScheduleEntry;
    index: number;
  }) => {
    if (overrideScheduleDates) {
      return overrideScheduleDates(repayment.date, index);
    }

    return formatDate(repayment.date);
  };

  return (
    <>
      <Table
        data-testid="repaymentScheduleTable"
        columns={[
          {
            id: 'date',
            header: 'Date',
            cell: ({ row }) => (
              <FormatRepaymentDate repayment={row.original} index={row.index} />
            ),
            footer: 'Total',
          },
          {
            id: 'principle',
            header: 'Outstanding amount',
            cell: ({ row }) => <>{formatCurrency(row.original.principal)}</>,
            footer: () => <>{formatCurrency(totalPrinciple)}</>,
          },
          {
            id: 'interest',
            header: 'Interest*',
            cell: ({ row }) => <>{formatCurrency(row.original.interest)}</>,
            footer: () => <>{formatCurrency(totalInterest)}</>,
          },
          {
            id: 'total',
            header: 'Total',
            cell: ({ row }) => <>{formatCurrency(row.original.total)}</>,
            footer: () => <>{formatCurrency(totalPayable)}</>,
          },
        ]}
        data={showAll ? schedule : schedule.slice(0, 3)}
      />
      {schedule.length > 3 && (
        <Button
          variant="tertiary"
          size="small"
          onClick={() => setShowAll(!showAll)}
          className="mt-2"
        >
          {showAll ? <span>Show less</span> : <span>View full schedule</span>}
        </Button>
      )}
    </>
  );
};
